import { Injectable, signal, computed, effect, inject, untracked } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { ToastService } from './toast.service';
import { CustomerAuthService } from './auth.service';

export interface WishItem {
  slug: string;
  name: string;
  category: string;
  price: number;
  originalPrice: number;
  img: string;
}

const GUEST_KEY = 'mmtess_guest_wishlist';

/**
 * Wishlist with backend persistence (My Wishlist `/wishlist`). Same model as the cart: guest
 * wishlist in localStorage while signed out, merged into the server wishlist on sign-in, pushed
 * on every change thereafter, cleared from memory on sign-out.
 */
@Injectable({ providedIn: 'root' })
export class WishlistService {
  private toast = inject(ToastService);
  private http = inject(HttpClient);
  private auth = inject(CustomerAuthService);
  private url = `${environment.customApiBase}/wishlist`;
  private _items = signal<WishItem[]>([]);
  private prevAuthed = false;

  readonly items = this._items.asReadonly();
  readonly count = computed(() => this._items().length);

  constructor() {
    this.prevAuthed = this.auth.isAuthenticated();
    if (!this.prevAuthed) this.restoreGuest();

    effect(() => {
      const authed = this.auth.isAuthenticated();
      untracked(() => {
        if (authed && !this.prevAuthed) this.onLogin();
        else if (!authed && this.prevAuthed) this.onLogout();
        this.prevAuthed = authed;
      });
    });

    if (this.prevAuthed) this.onLogin();
  }

  has(slug: string): boolean {
    return this._items().some(i => i.slug === slug);
  }

  toggle(item: WishItem) {
    const had = this.has(item.slug);
    this._items.update(list =>
      had ? list.filter(i => i.slug !== item.slug) : [...list, item],
    );
    this.persist();
    this.toast.show(had ? 'Removed from your wishlist.' : 'Product added to your wishlist.');
  }

  remove(slug: string) {
    this._items.update(list => list.filter(i => i.slug !== slug));
    this.persist();
  }

  // ── persistence ────────────────────────────────────────────────────────────
  private persist() {
    const items = this._items();
    if (this.auth.isAuthenticated()) {
      this.http.post(this.url, { op: 'replace', items }).subscribe({ error: () => {} });
    } else {
      try { localStorage.setItem(GUEST_KEY, JSON.stringify(items)); } catch { /* ignore quota */ }
    }
  }

  private onLogin() {
    this.http.post<any>(this.url, { op: 'merge', items: this._items() }).subscribe({
      next: (res) => {
        const wl = res?.data ?? res;
        this._items.set(this.fromApi(wl?.items ?? []));
        try { localStorage.removeItem(GUEST_KEY); } catch { /* ignore */ }
      },
      error: () => {},
    });
  }

  private onLogout() {
    this._items.set([]);
    try { localStorage.removeItem(GUEST_KEY); } catch { /* ignore */ }
  }

  private restoreGuest() {
    try {
      const raw = localStorage.getItem(GUEST_KEY);
      if (raw) this._items.set(JSON.parse(raw) as WishItem[]);
    } catch { /* ignore corrupt cache */ }
  }

  private fromApi(items: any[]): WishItem[] {
    return (items ?? []).map(i => ({
      slug: i.slug, name: i.name, category: i.category,
      price: i.price ?? 0, originalPrice: i.originalPrice ?? i.price ?? 0, img: i.img,
    }));
  }
}
